import { Popover } from '@base-ui/react/popover';
import { FC, useId, useMemo, useRef, useState } from 'react';

import {
  type ActivityMember,
  type GammaTeamRole,
  gammaTeamRoleDisplayMap,
} from '@/api/gamma-team/types';
import SearchIcon from '@/assets/svg/ui-elements/search-lg.svg';
import { Checkbox, Input, SelectTriggerButton, theme, Typography } from '@/design-system';

import {
  MemberPickerList,
  MemberPickerPopup,
  MemberPickerPositioner,
  MemberPickerRow,
  MemberPickerRowText,
  MemberPickerSearch,
} from './styled';
import { memberName } from './team-sorting';

export const ALL_TEAM_LABEL = 'All team';
export const FORMER_MEMBER_LABEL = 'Former member';
export const LOADING_MEMBER_LABEL = 'Loading…';
export const MEMBER_PICKER_LABEL = 'Team members';
export const NO_MEMBERS_MATCH = 'No team members match.';

const roleLabel = (role: string | null): string | undefined =>
  role ? (gammaTeamRoleDisplayMap[role as GammaTeamRole] ?? role) : undefined;

export const memberSupportText = (member: ActivityMember): string | undefined =>
  member.former ? FORMER_MEMBER_LABEL : roleLabel(member.role);

export const memberPickerLabel = (
  selected: readonly string[] | null,
  nameOf: (_id: string) => string | null,
): string => {
  if (selected === null) return LOADING_MEMBER_LABEL;
  if (selected.length === 0) return ALL_TEAM_LABEL;
  if (selected.length === 1) return nameOf(selected[0]) ?? LOADING_MEMBER_LABEL;
  return `${selected.length} members`;
};

const matchesSearch = (member: ActivityMember, query: string): boolean => {
  if (!query) return true;
  const haystack = [memberName(member), memberSupportText(member) ?? ''].join(' ').toLowerCase();
  return haystack.includes(query);
};

interface MemberRowProps {
  label: string;
  supportText?: string;
  checked: boolean;
  onToggle: () => void;
}

const MemberRow: FC<MemberRowProps> = ({ label, supportText, checked, onToggle }) => (
  <MemberPickerRow type="button" role="checkbox" aria-checked={checked} onClick={onToggle}>
    <Checkbox checked={checked} readOnly tabIndex={-1} size="sm" aria-hidden />
    <MemberPickerRowText>
      <Typography variant="body3" color={theme.colors.text.primary} noWrap>
        {label}
      </Typography>
      {supportText && (
        <Typography variant="body4" color={theme.colors.text.secondary} noWrap>
          {supportText}
        </Typography>
      )}
    </MemberPickerRowText>
  </MemberPickerRow>
);

interface TeamMemberPickerProps {
  selected: readonly string[] | null;
  options: ActivityMember[];
  currentMember: ActivityMember | null;
  disabled: boolean;
  onChange: (_ids: string[]) => void;
}

export const TeamMemberPicker: FC<TeamMemberPickerProps> = ({
  selected,
  options,
  currentMember,
  disabled,
  onChange,
}) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const searchId = useId();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const listed = useMemo((): ActivityMember[] => {
    const sorted = [...options].sort((a, b) => memberName(a).localeCompare(memberName(b)));
    const known = new Set(sorted.map((member) => member.userId));
    const unlisted = (selected ?? []).filter((id) => !known.has(id));
    for (const id of unlisted) {
      sorted.push(
        currentMember?.userId === id
          ? currentMember
          : { userId: id, name: LOADING_MEMBER_LABEL, role: null, former: false },
      );
    }
    return sorted;
  }, [options, selected, currentMember]);

  const nameOf = (id: string): string | null => {
    const member = listed.find((candidate) => candidate.userId === id);
    return member ? memberName(member) : null;
  };
  const label = memberPickerLabel(selected, nameOf);

  const query = search.trim().toLowerCase();
  const visible = listed.filter((member) => matchesSearch(member, query));
  const selectedSet = new Set(selected ?? []);

  const toggle = (id: string) => {
    onChange(
      selectedSet.has(id) ? [...selectedSet].filter((other) => other !== id) : [...selectedSet, id],
    );
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) setSearch('');
  };

  return (
    <>
      <SelectTriggerButton
        ref={triggerRef}
        label={label}
        ariaLabel={MEMBER_PICKER_LABEL}
        size="lg"
        open={open}
        disabled={disabled}
        onClick={() => handleOpenChange(!open)}
      />
      <Popover.Root open={open && !disabled} onOpenChange={handleOpenChange}>
        <Popover.Portal>
          <Popover.Positioner
            anchor={triggerRef}
            side="bottom"
            align="start"
            sideOffset={8}
            render={<MemberPickerPositioner />}
          >
            <Popover.Popup
              aria-label={MEMBER_PICKER_LABEL}
              initialFocus={searchRef}
              finalFocus={triggerRef}
              render={<MemberPickerPopup />}
            >
              <MemberPickerSearch>
                <Input
                  ref={searchRef}
                  id={searchId}
                  size="md"
                  startIcon={<SearchIcon />}
                  placeholder="Search team"
                  aria-label="Search team"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </MemberPickerSearch>
              <MemberPickerList role="group" aria-label={MEMBER_PICKER_LABEL}>
                {!query && (
                  <MemberRow
                    label={ALL_TEAM_LABEL}
                    checked={selectedSet.size === 0}
                    onToggle={() => onChange([])}
                  />
                )}
                {visible.map((member) => (
                  <MemberRow
                    key={member.userId}
                    label={memberName(member)}
                    supportText={memberSupportText(member)}
                    checked={selectedSet.has(member.userId)}
                    onToggle={() => toggle(member.userId)}
                  />
                ))}
                {visible.length === 0 && (
                  <Typography variant="body3" color={theme.colors.text.secondary} role="status">
                    {NO_MEMBERS_MATCH}
                  </Typography>
                )}
              </MemberPickerList>
            </Popover.Popup>
          </Popover.Positioner>
        </Popover.Portal>
      </Popover.Root>
    </>
  );
};
